
import { itemFactsOf, type ItemBonusView, type ItemFacts, type WireItemFacts } from "../itemfacts";
import { formatItemBonus } from "./itembonus";
import { formatYang } from "../money";
import itemDescTable from "./itemdesc.json";
import itemTooltipExtra from "./itemtooltipextra.json";
import { elem, styleInstaller } from "./chrome";

/** Re-exported from `client/src/itemfacts.ts`, which is where the whole item
 *  block is declared once. Two panels and `net/connection.ts` already import
 *  the name from here. */
export type { ItemBonusView };

/** An item as `tooltipLines` reads it: what it is (`ItemFacts`, every field
 *  optional because a panel's own fixture may build only the ones it is
 *  testing) plus the one thing that is the panel's rather than the item's --
 *  the price. */
export interface ItemTooltipData extends Partial<ItemFacts> {
  readonly vnum?: number;
  readonly name: string;
  /** 1 weapon, 2 armour, 3 potion, 4 anything else -- the same byte as
   *  `BagSlot.kind`, and what decides which of the blocks below apply. */
  readonly kind: number;
  readonly count: number;
  readonly price?: number;
}

export interface WireItemView extends WireItemFacts {
  readonly vnum?: number;
  readonly name: string;
  readonly kind: number;
  readonly count: number;
}

export interface SlotItemView extends Partial<ItemFacts> {
  readonly vnum?: number;
  readonly name: string;
  readonly kind: number;
  readonly count: number;
}

export function wireTooltipData(
  view: WireItemView,
  extra: Partial<ItemTooltipData> = {},
): ItemTooltipData {
  return {
    vnum: view.vnum,
    name: view.name,
    kind: view.kind,
    count: view.count,
    ...itemFactsOf(view),
    ...extra,
  };
}

/** The same mapping for the client's own spelling. See `wireTooltipData`. */
export function slotTooltipData(
  slot: SlotItemView,
  extra: Partial<ItemTooltipData> = {},
): ItemTooltipData {
  return {
    vnum: slot.vnum,
    name: slot.name,
    kind: slot.kind,
    count: slot.count,
    levelLimit: slot.levelLimit,
    attackMin: slot.attackMin,
    attackMax: slot.attackMax,
    defence: slot.defence,
    healHp: slot.healHp,
    healHpPct: slot.healHpPct,
    barredJobs: slot.barredJobs,
    bonuses: slot.bonuses,
    socket: slot.socket,
    socket1: slot.socket1,
    socket2: slot.socket2,
    ...extra,
  };
}

export type TooltipTone = "title" | "special" | "positive" | "negative" | "normal" | "muted";

export interface TooltipLine {
  readonly text: string;
  readonly tone: TooltipTone;
}

const JOB_NAMES: readonly string[] = ["Warrior", "Ninja", "Sura", "Shaman"];

function changeTone(value: number): TooltipTone {
  if (value > 0) return "positive";
  if (value < 0) return "negative";
  return "normal";
}

const TOOLTIP_EXTRA = itemTooltipExtra as unknown as {
  readonly weapons: Readonly<Record<string, { readonly min: number; readonly max: number; readonly fan?: boolean }>>;
  readonly armor: Readonly<Record<string, { readonly defence: number }>>;
  readonly metin: Readonly<Record<string, { readonly name: string; readonly bonus?: readonly [number, number] }>>;
  readonly unique: Readonly<Record<string, { readonly clock: "mall" | "unique" }>>;
  readonly costume: Readonly<Record<string, { readonly clock?: "mall" }>>;
  readonly applies: Readonly<Record<string, readonly (readonly [number, number])[]>>;
};

/** A fitted Metin stone's real name and proto row, by its own vnum -- the raw
 *  wire value a socket carries (`socketText`'s own comment). `undefined` for
 *  an unknown vnum (a socket a future stone the extract predates would fit),
 *  which is when the caller falls back to the raw-vnum reading. */
function metinStoneOf(vnum: number): { readonly name: string; readonly bonus?: readonly [number, number] } | undefined {
  return TOOLTIP_EXTRA.metin[String(vnum)];
}

/** An opened socket with no stone: METIN_SOCKET_TYPE_SILVER (1) or _GOLD (2),
 *  the values `CItem::AlterToSocketItem` writes (`item.cpp:1127-1137`). */
function isOpenedSocket(value: number): boolean {
  return value === 1 || value === 2;
}

function socketText(value: number): string {
  // TOOLTIP_SOCKET_EMPTY (`locale_game.txt:780` -- "Empty"), what
  // `uitooltip.py:1747` shows for an opened socket with no fitted item.
  if (isOpenedSocket(value)) return "Empty";
  const stone = metinStoneOf(value);
  if (stone !== undefined) return stone.name;
  return `fitted (${value})`;
}

function socketBonusLines(sockets: readonly (number | undefined)[]): TooltipLine[] {
  const lines: TooltipLine[] = [];
  for (const socket of sockets) {
    if (socket === undefined || socket === 0) continue;
    const stone = metinStoneOf(socket);
    if (stone === undefined) continue;
    const bonusText = stone.bonus === undefined ? null : formatItemBonus(stone.bonus[0], stone.bonus[1]);
    lines.push({ text: bonusText ?? "No effect", tone: "positive" });
  }
  return lines;
}

function magicAttackLine(vnum: number | undefined): { readonly line: TooltipLine; readonly fan: boolean } | null {
  if (vnum === undefined) return null;
  const entry = TOOLTIP_EXTRA.weapons[String(vnum)];
  if (entry === undefined) return null;
  const text = entry.max > entry.min
    ? `Magical Attack Value ${entry.min} - ${entry.max}`
    : `Magical Attack Value ${entry.min}`;
  return { line: { text, tone: "positive" }, fan: entry.fan === true };
}

function magicDefenceLine(vnum: number | undefined): TooltipLine | null {
  if (vnum === undefined) return null;
  const entry = TOOLTIP_EXTRA.armor[String(vnum)];
  if (entry === undefined) return null;
  return { text: `Magical Defence ${entry.defence}`, tone: "positive" };
}

function secondToDHM(totalSeconds: number): string {
  if (totalSeconds < 60) return `0${MINUTE_WORD}`;
  const second = Math.floor(totalSeconds % 60);
  const minute = Math.floor((totalSeconds / 60) % 60);
  const hour = Math.floor(totalSeconds / 60 / 60) % 24;
  const day = Math.floor(Math.floor(totalSeconds / 60 / 60) / 24);
  void second; // SecondToDHM reads it into a local and never prints it either.
  let text = "";
  if (day > 0) text += `${day}${DAY_WORD} `;
  if (hour > 0) text += `${hour}${HOUR_WORD} `;
  if (minute > 0) text += `${minute}${MINUTE_WORD}`;
  return text;
}

// `DAY`/`HOUR`/`MINUTE` (`locale_game.txt:69,210,303` -- "Days"/"Hours"/"Minutes").
const DAY_WORD = "Days";
const HOUR_WORD = "Hours";
const MINUTE_WORD = "Minutes";

/** A costume vnum (`uitooltip.py:945`), whose sockets are never drawn as a metin-slot list. */
function isCostume(vnum: number | undefined): boolean {
  return vnum !== undefined && TOOLTIP_EXTRA.costume[String(vnum)] !== undefined;
}

function remainingTimeLine(vnum: number | undefined, socket: number | undefined, nowSeconds: number): TooltipLine | null {
  if (vnum === undefined || socket === undefined || socket <= 0) return null;
  const clock = TOOLTIP_EXTRA.unique[String(vnum)]?.clock ?? TOOLTIP_EXTRA.costume[String(vnum)]?.clock;
  if (clock === undefined) return null;
  // AppendMallItemLastTime (`uitooltip.py:1762-1765`) or AppendUniqueItemLastTime (`:1757-1760`).
  const remainingSeconds = clock === "mall" ? Math.max(0, socket - nowSeconds) : socket * 60;
  return { text: `Time Remaining : ${secondToDHM(remainingSeconds)}`, tone: "normal" };
}

const BLEND_ITEMS: Readonly<Record<number, string>> = {
  50821: "Chance of critical hit", 50822: "Piercing chance", 50823: "Attack Speed",
  50824: "Magic Resistance", 50825: "Attack bonus", 50826: "Defence bonus", 51002: "Energy",
};

/** Decode the server's blend result from the legacy socket-0/1/2
 * apply/value/duration triple (`blend_item.cpp:225-227`). */
export function blendPotencyText(
  vnum: number | undefined,
  socket: number | undefined,
  socket1: number | undefined,
  socket2: number | undefined,
): string | null {
  const label = vnum === undefined ? undefined : BLEND_ITEMS[vnum];
  const applies: Readonly<Record<number, number>> = {
    50821: 40, 50822: 41, 50823: 17, 50824: 77,
    50825: 93, 50826: 94, 51002: 128,
  };
  if (
    label === undefined || socket === undefined || socket1 === undefined ||
    socket2 === undefined || socket !== applies[vnum ?? 0]
  ) return null;
  const value = socket1;
  const duration = socket2;
  if (value === 0 || duration === 0) return null;
  return `${label} +${value}${vnum === 51002 ? "" : "%"} for ${duration >= 3600 ? `${Math.floor(duration / 3600)}h` : `${Math.floor(duration / 60)}m`}`;
}

/** Which classes a `barredJobs` mask still allows, in the fixed order above. */
export function wearableJobs(barredJobs: number): string[] {
  return JOB_NAMES.filter((_, index) => (barredJobs & (1 << index)) === 0);
}

export function itemDescFor(vnum: number | undefined): readonly [string, string] | undefined {
  if (vnum === undefined || vnum === 0) return undefined;
  const row = (itemDescTable as Record<string, string[]>)[String(vnum)];
  if (row === undefined) return undefined;
  return [row[0] ?? "", row[1] ?? ""];
}

export function tooltipLines(data: ItemTooltipData, nowMs: number = Date.now()): readonly TooltipLine[] {
  const lines: TooltipLine[] = [];
  const title = data.count > 1 ? `${data.name} x${data.count}` : data.name;
  lines.push({ text: title, tone: data.bonuses && data.bonuses.length > 0 ? "special" : "title" });

  const lore = itemDescFor(data.vnum);
  if (lore !== undefined && lore[0] !== "") {
    lines.push({ text: lore[0], tone: "muted" });
  }
  if (lore !== undefined && lore[1] !== "") {
    lines.push({ text: lore[1], tone: "normal" });
  }

  if (data.levelLimit !== undefined && data.levelLimit > 0) {
    lines.push({ text: `From Level: ${data.levelLimit}`, tone: "normal" });
  }

  if (data.kind === 1 && data.attackMin !== undefined && data.attackMax !== undefined) {
    const text =
      data.attackMax > data.attackMin
        ? `Attack Value ${data.attackMin} - ${data.attackMax}`
        : `Attack Value ${data.attackMin}`;
    const attackLine: TooltipLine = { text, tone: "positive" };
    const magic = data.kind === 1 ? magicAttackLine(data.vnum) : null;
    if (magic !== null && magic.fan) lines.push(magic.line);
    lines.push(attackLine);
    if (magic !== null && !magic.fan) lines.push(magic.line);
  } else if (data.kind === 1) {
    const magic = magicAttackLine(data.vnum);
    if (magic !== null) lines.push(magic.line);
  }

  if (data.kind === 2 && data.defence !== undefined && data.defence !== 0) {
    lines.push({ text: `Defence ${data.defence}`, tone: changeTone(data.defence) });
  }
  if (data.kind === 2) {
    const magicDefence = magicDefenceLine(data.vnum);
    if (magicDefence !== null) lines.push(magicDefence);
  }

  // `__AppendAffectInformation`, the item_proto's own applies, before the
  // rolled attributes (uitooltip.py:898-899, :916-917, :929-930, :939-940, :947-948).
  for (const [apply, value] of TOOLTIP_EXTRA.applies[String(data.vnum)] ?? []) {
    const text = formatItemBonus(apply, value);
    if (text !== null) lines.push({ text, tone: changeTone(value) });
  }

  for (const bonus of data.bonuses ?? []) {
    const text = formatItemBonus(bonus.apply, bonus.value);
    if (text === null) continue;
    lines.push({ text, tone: changeTone(bonus.value) });
  }

  const sockets = [data.socket, data.socket1, data.socket2];
  const blend = blendPotencyText(data.vnum, data.socket, data.socket1, data.socket2);
  if (blend !== null) lines.push({ text: blend, tone: "special" });
  // A closed socket (0) is METIN_SOCKET_TYPE_NONE and is not drawn, and the
  // list is drawn only when some socket holds an item rather than an opened
  // slot (`uitooltip.py:1613-1637`).
  const drawn = sockets.map((socket) => socket ?? 0).filter((socket) => socket !== 0);
  // The costume branch (`uitooltip.py:945-963`) never calls `__AppendMetinSlotInfo`.
  if (!isCostume(data.vnum) && drawn.some((socket) => !isOpenedSocket(socket))) {
    lines.push({
      text: `Sockets: ${drawn.map(socketText).join(", ")}`,
      tone: "muted",
    });
    lines.push(...socketBonusLines(sockets));
  }

  if (data.kind === 3) {
    if (data.healHp !== undefined && data.healHp > 0) {
      lines.push({ text: `HP +${data.healHp}`, tone: "positive" });
    }
    if (data.healHpPct !== undefined && data.healHpPct > 0) {
      lines.push({ text: `HP +${data.healHpPct}%`, tone: "positive" });
    }
  }

  if (data.kind === 4) {
    const remaining = remainingTimeLine(data.vnum, data.socket, Math.floor(nowMs / 1000));
    if (remaining !== null) lines.push(remaining);
  }

  if ((data.kind === 1 || data.kind === 2) && data.barredJobs !== undefined) {
    const jobs = wearableJobs(data.barredJobs);
    lines.push({ text: "[ Wearable ]", tone: "muted" });
    lines.push({ text: jobs.length > 0 ? jobs.join(" ") : "Nobody", tone: "normal" });
  }

  if (data.price !== undefined && data.price > 0) {
    // TOOLTIP_SELLPRICE/TOOLTIP_BUYPRICE are both "Price: %s" (`locale_game.txt:757,629`),
    // and the `%s` is `NumberToMoneyString(price)` (`uitooltip.py:1494,1505`) --
    // dot-grouped digits plus MONETARY_UNIT0 (`:304` -- "Yang"), not "gold".
    lines.push({ text: `Price: ${formatYang(data.price)}`, tone: "normal" });
  } else if (data.price !== undefined) {
    // TOOLTIP_ANTI_SELL (`locale_game.txt:567`), shown by `AppendSellingPrice`
    // (`uitooltip.py:1500-1505`) instead of a price line for an unsellable item --
    // exactly the zero-price state `ShopSlot`'s docstring describes.
    lines.push({ text: "Cannot be sold in the shop.", tone: "muted" });
  }

  return lines;
}

export function computeTooltipPosition(
  pointerX: number,
  pointerY: number,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): { x: number; y: number } {
  // Legacy ItemToolTip.OnUpdate uses +40 below and -30 above the pointer
  // (`uitooltip.py:279-289`). Keep the asymmetric source spacing.
  const y = pointerY < viewportHeight - 300 ? pointerY + 40 : pointerY - height - 30;
  const x = pointerX - width / 2;
  return {
    x: Math.max(0, Math.min(x, Math.max(0, viewportWidth - width))),
    y: Math.max(0, Math.min(y, Math.max(0, viewportHeight - height))),
  };
}

const STYLE_ID = "item-tooltip-style";

const STYLE = `
.item-tooltip {
  position: fixed;
  z-index: 500;
  pointer-events: none;
  max-width: 220px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(10, 11, 15, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5);
  font: 12px/1.5 system-ui, sans-serif;
}
.item-tooltip-line { white-space: normal; }
.item-tooltip-line-title { color: #f2e9c2; font-weight: 600; margin-bottom: 2px; }
.item-tooltip-line-special { color: #ffc800; font-weight: 600; margin-bottom: 2px; }
.item-tooltip-line-positive { color: #8aba8e; }
.item-tooltip-line-negative { color: #e5796b; }
.item-tooltip-line-normal { color: #c2c2c2; }
.item-tooltip-line-muted { color: #83898f; font-size: 0.92em; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class ItemTooltip {
  private readonly root: HTMLElement;

  constructor(doc: Document = document) {
    installStyle(doc);
    this.root = elem(doc, "div", "item-tooltip");
    this.root.hidden = true;
    doc.body.append(this.root);
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  /** Draw `data` and place the panel at a pointer position. */
  show(data: ItemTooltipData, pointerX: number, pointerY: number): void {
    const doc = this.root.ownerDocument;
    this.root.replaceChildren();
    for (const line of tooltipLines(data)) {
      const el = elem(doc, "div", `item-tooltip-line item-tooltip-line-${line.tone}`);
      // Server data, never markup -- the same rule the bag and the chat box
      // follow for a name, restated here for a whole sentence.
      el.textContent = line.text;
      this.root.append(el);
    }
    this.root.hidden = false;
    this.reposition(pointerX, pointerY);
  }

  /** Follow the pointer without rebuilding the content. A no-op while hidden,
   *  so a stray move after `hide()` cannot flash the panel back on. */
  move(pointerX: number, pointerY: number): void {
    if (this.root.hidden) return;
    this.reposition(pointerX, pointerY);
  }

  hide(): void {
    this.root.hidden = true;
  }

  /** Remove the node entirely. For a panel that is itself being torn down. */
  destroy(): void {
    this.root.remove();
  }

  private reposition(pointerX: number, pointerY: number): void {
    const view = this.root.ownerDocument.defaultView;
    const viewportWidth = view?.innerWidth ?? 1024;
    const viewportHeight = view?.innerHeight ?? 768;
    // `offsetWidth`/`offsetHeight` are zero without a layout engine under them
    // (this project's unit tests run in `happy-dom`, which does not lay pages
    // out) -- falling back to the panel's own `max-width` and a generous
    // guess keeps the maths sane there without a real browser to measure in.
    const width = this.root.offsetWidth || 190;
    const height = this.root.offsetHeight || 60;
    const { x, y } = computeTooltipPosition(pointerX, pointerY, width, height, viewportWidth, viewportHeight);
    this.root.style.left = `${x}px`;
    this.root.style.top = `${y}px`;
  }
}

export function attachHoverTooltip(
  cell: HTMLElement,
  tooltip: ItemTooltip,
  dataFn: () => ItemTooltipData | null,
): void {
  cell.addEventListener("pointerenter", (event) => {
    if (event.pointerType !== "mouse") return;
    const data = dataFn();
    if (data === null) return;
    tooltip.show(data, event.clientX, event.clientY);
  });
  cell.addEventListener("pointermove", (event) => {
    if (event.pointerType !== "mouse" || !tooltip.visible) return;
    tooltip.move(event.clientX, event.clientY);
  });
  cell.addEventListener("pointerleave", () => tooltip.hide());
}
